import { ICreatePhaseItemResponse } from 'redux-store/api/schedule-phases-items';
import { IPhase, IPhaseItem, PhaseStatusesEnum } from 'redux-store/types';
import { rowElementColorHelper } from 'utils/helpers/scheduleHelpsers';
import { ISchedulePhase } from 'redux-store/api/schedule-phases';
import { scheduler } from 'dhtmlx-scheduler/dhtmlxscheduler';
import { SetURLSearchParams } from 'react-router-dom';
import { IProject } from 'redux-store/api/projects';
import { CalendarViewModeEnum } from 'utils/types';
import { ItemTypeEnum } from './types';
import dayjs from 'dayjs';

const getPhaseDates = (phase: IPhase): { startDate: string; endDate: string } | null => {
  if (!phase.items.length) return null;

  const sortedByStart = [...phase.items].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  const sortedByEnd = [...phase.items].sort(
    (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  );

  return {
    startDate: sortedByStart[0].startDate,
    endDate: sortedByEnd[0].endDate,
  };
};

export const parseProjectData = (phases: IPhase[], showPhases: boolean) => {
  if (phases) {
    const phasesList = phases.map((phase) => {
      const phaseDates = getPhaseDates(phase);

      const start = !!phaseDates?.startDate ? phaseDates.startDate : phase?.startDate;
      const end = !!phaseDates?.endDate ? phaseDates.endDate : phase?.endDate;

      const startDate = dayjs.utc(start).startOf('day');
      const endDate = dayjs.utc(end).add(1, 'day').startOf('day');

      return {
        id: phase.id,
        text: phase.title,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        item_type: ItemTypeEnum.PHASE,
        color: phase.color,
        is_loading: false,
        original_event_data: JSON.stringify(phase),
      };
    });

    const phaseMap = Object.fromEntries(
      phases.map((phase) => [phase.id, { title: phase.title, color: phase.color, id: phase.id }])
    );

    const formatedPhaseList = phases
      ?.flatMap(({ items }) => items)
      ?.sort((a, b) => Date.parse(a?.startDate) - Date.parse(b?.startDate));

    const formattedItemsList = formatedPhaseList?.map((item) => {
      const start = dayjs.utc(item.startDate).startOf('day');
      const end = dayjs.utc(item.endDate).add(1, 'day').startOf('day');

      const parent = phaseMap[item.phaseId];

      return {
        id: item.id,
        text: item.title,
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD'),
        item_type: ItemTypeEnum.SCHEDULE_ITEM,
        parent_title: parent.title,
        parent_color: parent.color,
        parent_id: parent.id,
        is_loading: false,
        original_event_data: JSON.stringify(item),
      };
    });

    if (!showPhases) {
      return [...formattedItemsList];
    }

    return [...phasesList, ...formattedItemsList];
  }

  return [];
};

export const getTodaysBlock = function (date: Date): string {
  const today = new Date();
  const day = date.getDay();

  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return 'dhx_month_today';
  }

  if (day === 6 || day === 0) {
    return 'dhx_month_weekend';
  }

  return '';
};

const itemLoader = `<svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_OSmW{transform-origin:center;animation:spinner_T6mA .75s step-end infinite}@keyframes spinner_T6mA{8.3%{transform:rotate(30deg)}16.6%{transform:rotate(60deg)}25%{transform:rotate(90deg)}33.3%{transform:rotate(120deg)}41.6%{transform:rotate(150deg)}50%{transform:rotate(180deg)}58.3%{transform:rotate(210deg)}66.6%{transform:rotate(240deg)}75%{transform:rotate(270deg)}83.3%{transform:rotate(300deg)}91.6%{transform:rotate(330deg)}100%{transform:rotate(360deg)}}</style><g class="spinner_OSmW"><rect x="11" y="1" width="2" height="5" opacity=".14"/><rect x="11" y="1" width="2" height="5" transform="rotate(30 12 12)" opacity=".29"/><rect x="11" y="1" width="2" height="5" transform="rotate(60 12 12)" opacity=".43"/><rect x="11" y="1" width="2" height="5" transform="rotate(90 12 12)" opacity=".57"/><rect x="11" y="1" width="2" height="5" transform="rotate(120 12 12)" opacity=".71"/><rect x="11" y="1" width="2" height="5" transform="rotate(150 12 12)" opacity=".86"/><rect x="11" y="1" width="2" height="5" transform="rotate(180 12 12)"/></g></svg>`;
const checklistIcon = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.92651 12.5156L3.69952 12.9611L3.69952 12.9611L3.92651 12.5156ZM2.65188 11.241L2.20638 11.468L2.20638 11.468L2.65188 11.241ZM11.3494 11.241L11.7949 11.468L11.7949 11.468L11.3494 11.241ZM10.0748 12.5156L10.3018 12.9611L10.3018 12.9611L10.0748 12.5156ZM5.89587 7.52161C5.70061 7.32635 5.38403 7.32635 5.18876 7.52161C4.9935 7.71687 4.9935 8.03345 5.18876 8.22872L5.89587 7.52161ZM6.41732 8.75016L6.06376 9.10372C6.15753 9.19748 6.28471 9.25016 6.41732 9.25016C6.54993 9.25016 6.6771 9.19748 6.77087 9.10372L6.41732 8.75016ZM9.1042 6.77038C9.29947 6.57512 9.29947 6.25854 9.1042 6.06328C8.90894 5.86801 8.59236 5.86801 8.3971 6.06328L9.1042 6.77038ZM11.1673 4.41683V8.16683H12.1673V4.41683H11.1673ZM2.83398 8.16683V4.41683H1.83398V8.16683H2.83398ZM7.00065 12.3335C6.17566 12.3335 5.58698 12.3331 5.12571 12.2954C4.67027 12.2582 4.38323 12.1871 4.15351 12.0701L3.69952 12.9611C4.09371 13.162 4.52699 13.2498 5.04427 13.2921C5.55572 13.3339 6.19216 13.3335 7.00065 13.3335V12.3335ZM1.83398 8.16683C1.83398 8.97532 1.8336 9.61176 1.87538 10.1232C1.91765 10.6405 2.00553 11.0738 2.20638 11.468L3.09739 11.014C2.98034 10.7843 2.90927 10.4972 2.87206 10.0418C2.83437 9.5805 2.83398 8.99182 2.83398 8.16683H1.83398ZM4.15351 12.0701C3.69878 11.8384 3.32908 11.4687 3.09739 11.014L2.20638 11.468C2.53395 12.1109 3.05663 12.6335 3.69952 12.9611L4.15351 12.0701ZM11.1673 8.16683C11.1673 8.99182 11.1669 9.5805 11.1292 10.0418C11.092 10.4972 11.021 10.7843 10.9039 11.014L11.7949 11.468C11.9958 11.0738 12.0837 10.6405 12.1259 10.1232C12.1677 9.61176 12.1673 8.97532 12.1673 8.16683H11.1673ZM7.00065 13.3335C7.80914 13.3335 8.44558 13.3339 8.95703 13.2921C9.47431 13.2498 9.9076 13.162 10.3018 12.9611L9.84779 12.0701C9.61807 12.1871 9.33104 12.2582 8.8756 12.2954C8.41432 12.3331 7.82564 12.3335 7.00065 12.3335V13.3335ZM10.9039 11.014C10.6722 11.4687 10.3025 11.8384 9.84779 12.0701L10.3018 12.9611C10.9447 12.6335 11.4674 12.1109 11.7949 11.468L10.9039 11.014ZM5.83406 1.0835H8.16739V0.0834961H5.83406V1.0835ZM8.16739 2.41683H5.83406V3.41683H8.16739V2.41683ZM5.83406 2.41683C5.46587 2.41683 5.16739 2.11835 5.16739 1.75016H4.16739C4.16739 2.67064 4.91358 3.41683 5.83406 3.41683V2.41683ZM8.83406 1.75016C8.83406 2.11835 8.53558 2.41683 8.16739 2.41683V3.41683C9.08786 3.41683 9.83406 2.67064 9.83406 1.75016H8.83406ZM8.16739 1.0835C8.53558 1.0835 8.83406 1.38197 8.83406 1.75016H9.83406C9.83406 0.829688 9.08786 0.0834961 8.16739 0.0834961V1.0835ZM5.83406 0.0834961C4.91358 0.0834961 4.16739 0.829689 4.16739 1.75016H5.16739C5.16739 1.38197 5.46587 1.0835 5.83406 1.0835V0.0834961ZM5.18876 8.22872L6.06376 9.10372L6.77087 8.39661L5.89587 7.52161L5.18876 8.22872ZM6.77087 9.10372L9.1042 6.77038L8.3971 6.06328L6.06376 8.39661L6.77087 9.10372ZM2.83398 4.41683C2.83398 3.30497 3.67191 2.38811 4.75045 2.26441L4.6365 1.27092C3.05889 1.45186 1.83398 2.79095 1.83398 4.41683H2.83398ZM12.1673 4.41683C12.1673 2.79095 10.9424 1.45186 9.3648 1.27092L9.25085 2.26441C10.3294 2.38811 11.1673 3.30497 11.1673 4.41683H12.1673Z" fill="#6E707A"></path></svg>`;
const completeIcon = `<svg width="18" height="18" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path
  d="M5.5026 0.895508C2.6851 0.895508 0.398438 3.18217 0.398438 5.99967C0.398438 8.81717 2.6851 11.1038 5.5026 11.1038C8.3201 11.1038 10.6068 8.81717 10.6068 5.99967C10.6068 3.18217 8.3201 0.895508 5.5026 0.895508ZM7.55958 5.08093L5.17593 7.45946C5.10447 7.53602 5.00749 7.57177 4.90541 7.57177C4.80843 7.57177 4.71145 7.53602 4.63489 7.45946L3.44563 6.27021C3.2976 6.12219 3.2976 5.87716 3.44563 5.72914C3.59365 5.58112 3.83865 5.58112 3.98667 5.72914L4.90541 6.64791L7.01854 4.53989C7.16656 4.38677 7.41156 4.38677 7.55958 4.53989C7.7076 4.68791 7.7076 4.92781 7.55958 5.08093Z"
  fill="#F9F9F9"
/>
</svg>`;
const completedItemStyles = {
  color: '#BCBDC3',
  resize: '#BCBDC3',
  text: 'white',
  light: '#BCBDC3',
  itemText: 'white',
  phaseText: 'white',
  resizeButton: '#BCBDC3',
};

const scheduleItemEventBox = (
  text: string,
  is_loading: boolean,
  item: IPhaseItem,
  color?: string,
  projectName?: string
) => {
  const isCompleted = item.status === PhaseStatusesEnum.COMPLETED;
  const colors = isCompleted ? completedItemStyles : rowElementColorHelper(color);

  const checklist = item?.checklist
    ? `<div class="checklist-icon-container" id="checklist-button" data-id="${item.id}">
          ${checklistIcon}
          ${item?.checklist?.completedItemsCount}/${item?.checklist?.itemsCount}
      </div>`
    : '';

  const colorIndicator = !!color
    ? `<div class="scheduler_phase_item_color" 
          style="background: ${colors.color}">
     </div>`
    : '';

  const loader = is_loading
    ? `<div style="width: 16px; height: 16px; display: flex; justify-cotnent: center; align-items: center">${itemLoader}</div>`
    : '';

  return `
  <div class="scheduler_phase_item_box_container"
       style="background: ${colors.light};
              display: flex;
              justify-content: flex-start;
              align-items: center;
              color: ${colors.itemText};
              border: 1px solid ${colors.color}"
  >
    <div class="scheduler_phase_item_text_content">
          ${isCompleted ? completeIcon : colorIndicator}
          ${loader}
          <div class="scheduler_phase_item_title">${text} ${!!projectName ? '| ' + projectName : ''}</div>
          ${checklist}
    </div>
  </div>`;
};

const phaseItemEventBox = (text: string, color?: string) => {
  const colors = rowElementColorHelper(color);

  return `
  <div class="scheduler_phase_item_box_container"
       style="background: ${colors.color};;
              display: flex;
              justify-content: flex-start;
              align-items: flex-start;
              color: ${colors.phaseText}">
      <div>${text}</div>
  </div>`;
};

export const setEventText = function (start: Date, end: Date, event: any): string {
  if (event.item_type === ItemTypeEnum.SCHEDULE_ITEM) {
    const eventData: IPhaseItem = JSON.parse(event.original_event_data);

    return scheduleItemEventBox(
      event?.text,
      event?.is_loading,
      eventData,
      event?.parent_color,
      event?.project_name
    );
  }

  return phaseItemEventBox(event.text, event.color);
};

export const getPhase = (phaseId: number, phases: IPhase[]) => {
  const phase = phases?.find(({ id }) => id === phaseId);

  if (!phase) return null;

  return phase;
};

export const getScheduleItem = (scheduleItemId: number, phases: IPhase[]) => {
  const itemsList = phases?.flatMap(({ items }) => items);
  const scheduleItem = itemsList?.find(({ id }) => id === scheduleItemId);

  if (!scheduleItem) return null;

  return scheduleItem;
};

export const setMonthScaleDate = (date: Date) => {
  const dayName = scheduler.date.date_to_str('%D')(date);
  return `${dayName}`;
};

export const setWeekScaleDate = (date: Date) => {
  const isToday = getTodaysBlock(date);

  const monthName = scheduler.date.date_to_str('%M')(date);
  const dayName = scheduler.date.date_to_str('%D')(date);
  const dateStr = scheduler.date.date_to_str('%j')(date);

  if (!!isToday) {
    return `<div class='${isToday}'>${dayName}<br>${monthName} ${dateStr}</div>`;
  }

  return `${dayName}<br>${monthName} ${dateStr}`;
};

export const handleChangeView = (mode: CalendarViewModeEnum, lastMode: string) => {
  if (mode === 'day' || mode === 'week') {
    scheduler.xy.bar_height = 42;
  } else {
    scheduler.xy.bar_height = 30;
  }
};

export const showFullDayEvents = (id, event) => {
  return !event._timed;
};

export const updateCalendarEvent = (
  eventId: string,
  newItem: IPhaseItem | IPhase,
  isPhase: boolean,
  parentColor?: string
) => {
  const oldEvent = scheduler.getEvent(eventId);
  const shortedStartDate = dayjs.utc(newItem.startDate).startOf('day');
  const shortedEndDate = dayjs.utc(newItem.endDate).startOf('day');

  const start = dayjs(shortedStartDate.format('YYYY-MM-DD')).startOf('day');
  const end = dayjs(shortedEndDate.format('YYYY-MM-DD')).add(1, 'day').startOf('day');

  if (!!oldEvent) {
    Object.assign(oldEvent, {
      text: newItem.title,
      start_date: start.toDate(),
      end_date: end.toDate(),
      original_event_data: JSON.stringify(newItem),
      ...(newItem.color && isPhase && { color: newItem.color }),
      ...(parentColor && !isPhase && { parent_color: parentColor }),
    });

    scheduler.updateEvent(eventId);
  }
};

export const updateItemsByActualProject = (actualProject: IProject) => {
  const isPhasesExist = actualProject.phases.length;

  if (!isPhasesExist) return;

  actualProject.phases.forEach((phase) => {
    updateCalendarEvent(String(phase?.id), phase, true);

    phase?.items?.forEach((scheduleItem) => {
      setTimeout(() => {
        updateCalendarEvent(String(scheduleItem?.id), scheduleItem, false, phase?.color);
      }, 100);
    });
  });
};

export const formatToCalendarPhase = (phase: ISchedulePhase) => {
  const startDate = dayjs.utc(phase.startDate).startOf('day');
  const endDate = dayjs.utc(phase.endDate).add(1, 'day').startOf('day');

  const formatedScheduleItem = {
    id: String(phase.id),
    text: phase.title,
    start_date: startDate.format('YYYY-MM-DD'),
    end_date: endDate.format('YYYY-MM-DD'),
    item_type: ItemTypeEnum.PHASE,
    color: phase.color,
    is_loading: false,
    original_event_data: JSON.stringify(phase),
  };

  return formatedScheduleItem;
};

export const setDateRange = (
  currentDate: string | Date,
  calendarMode: CalendarViewModeEnum,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
) => {
  const date = dayjs.utc(currentDate);

  switch (calendarMode) {
    case CalendarViewModeEnum.MONTH:
      const startOfMonth = dayjs(date).startOf('month').format('YYYY-MM-DD');
      searchParams.set('show-date', startOfMonth);
      break;

    case CalendarViewModeEnum.WEEK:
      const startOfWeek = dayjs(date).startOf('isoWeek').format('YYYY-MM-DD');
      searchParams.set('show-date', startOfWeek);
      break;

    case CalendarViewModeEnum.DAY:
      const currentDay = date.format('YYYY-MM-DD');
      searchParams.set('show-date', currentDay);
      break;
  }

  setSearchParams(searchParams);
};

let lastMoreEventsClickPosition = { x: 0, y: 0 };

export const handleMoreEventsClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (target?.classList.contains('more-events')) {
    lastMoreEventsClickPosition = {
      x: e.clientX,
      y: e.clientY,
    };
  }
};

export const getLastClickPosition = () => {
  return lastMoreEventsClickPosition;
};

export const getVisibleEventsForDateBlock = (blockRect: DOMRect): any[] => {
  const calData = document.querySelector('.dhx_cal_data');
  if (!calData) return [];
  const eventElements = calData.querySelectorAll('.dhx_cal_event_line');

  const visibleEvents: any[] = [];

  eventElements.forEach((el: any) => {
    const rect = el.getBoundingClientRect();

    if (rect.y > blockRect.y && rect.y + 28 < blockRect.y + 200) {
      const eventId = el.getAttribute('event_id');

      if (eventId) {
        const event = scheduler.getEvent(eventId);
        visibleEvents.push(event);
      }
    }
  });
  return visibleEvents;
};

const filterByMatchingIds = (dayEvents, weekVisibleEvents) => {
  const idSet = new Set(dayEvents.map((obj) => obj.id));
  return weekVisibleEvents?.filter((obj) => idSet.has(obj.id));
};

export const getVisibleEventsInMonthDayBlock = (date: Date) => {
  const dayEvents = scheduler.getEvents(date, new Date(date.getTime() + 24 * 60 * 60 * 1000));
  const cells = Array.from(document.querySelectorAll('.dhx_cal_month_cell'));
  const formattedDate = dayjs.utc(date).format('YYYY-MM-DD');

  for (const cell of cells) {
    const cellDate = cell.getAttribute('data-cell-date');
    if (cellDate && cellDate.includes(formattedDate)) {
      const blockRect = cell.getBoundingClientRect();
      const dateBlockItems = getVisibleEventsForDateBlock(blockRect);

      const dayVisibleEvents = filterByMatchingIds(dayEvents, dateBlockItems);

      return dayVisibleEvents;
    }
  }

  return [];
};

export const setMonthEventLink = (date: Date, count: number) => {
  return `<div class="more-events" data-items-count="${count}" data-date-to-pass="${date.toString()}" data-date="${date.toISOString()}">  more items...</div>`;
};

export const changeSetMoreEventsLinkText = () => {
  const calData = document.querySelector('.dhx_cal_data');
  const moreEventElements = calData.querySelectorAll('.more-events');

  moreEventElements?.forEach((el) => {
    const countAttr = el.getAttribute('data-items-count');
    const dateAttr = el.getAttribute('data-date-to-pass');

    if (dateAttr) {
      const dateObj = new Date(dateAttr);
      const items = getVisibleEventsInMonthDayBlock(dateObj);

      if (!!countAttr && items.length) {
        const countToShow = Number(countAttr) - items.length;

        el.textContent = `${countToShow} more item${countToShow === 1 ? '' : 's'}...`;
      }
    }
  });
};

export const formatToCalendarItem = (scheduleItem: ICreatePhaseItemResponse, parent: IPhase) => {
  const shortedStartDate = dayjs.utc(scheduleItem.startDate).startOf('day');
  const shortedEndDate = dayjs.utc(scheduleItem.endDate).startOf('day');

  const start = dayjs.utc(shortedStartDate.format('YYYY-MM-DD')).startOf('day');
  const end = dayjs.utc(shortedEndDate.format('YYYY-MM-DD')).add(1, 'day').startOf('day');

  const formatedScheduleItem = {
    id: String(scheduleItem.id),
    text: scheduleItem.title,
    start_date: start.toDate(),
    end_date: end.toDate(),
    item_type: ItemTypeEnum.SCHEDULE_ITEM,
    parent_title: parent.title,
    parent_color: parent.color,
    parent_id: parent.id,
    is_loading: false,
    original_event_data: JSON.stringify(scheduleItem),
  };

  return formatedScheduleItem;
};
